import { useIonRouter } from '@ionic/react';

import { cache, mutate } from 'swr';

import { login as loginReq } from '../../axios/Auth/index';
import { logout as logoutReq } from '../../axios/Auth/index';
import { register as registerReq } from '../../axios/Auth/index';
import { password as passwordReq } from '../../axios/Auth/index';
import { GET_SINIESTROS, PERSONAL_DATA, PETS } from '../../axios/Utils/constants';
import { useAuth as useAuthContext } from '../../context/AuthContext';
import { ApiRes, LoginReq, LogoutReq, PasswordReq, RegisterReq } from '../../types/Auth/types';
import { handleAuthRequest } from '../../utils/auth';
import {
  activatePendingDiagnosticsAccessForLogin,
  clearActiveDiagnosticsAccess,
} from '../../utils/diagnosticsAccess';
import { getLoginErrorMessage } from '../../utils/apiHealth';
import { flushTrackingBeforeLogout } from '../../utils/tracking';
import { schedulePostLoginNotificationPermissionPrompt } from '../../utils/notificationPermissionPrompt';
import { usePushNotifications } from '../Utils/usePushNotifications';
import { useStorage } from '../Utils/useStorage';

export const STORAGE_KEYS = {
  token: 'TOKEN',
  refreshToken: 'REFRESH_TOKEN',
} as const;

/**
 * Limpia el caché de SWR para evitar que datos del usuario anterior
 * persistan al cambiar de sesión
 */
const clearAllSWRCache = (): void => {
  // Limpiar keys específicas del usuario con mutate
  mutate(PERSONAL_DATA, undefined, false);
  mutate(PETS, undefined, false);
  mutate(GET_SINIESTROS, undefined, false);

  // Intentar limpiar todo el caché de SWR
  try {
    // Método 1: cache.clear() si existe
    if (cache && typeof cache.clear === 'function') {
      cache.clear();
    }

    // Método 2: Iterar sobre todas las keys y eliminarlas
    if (cache && typeof cache.keys === 'function') {
      const keys = cache.keys();
      for (const key of keys) {
        mutate(key, undefined, false);
      }
    }
  } catch (e) {
    console.warn('clearAllSWRCache:', e);
  }
};

export const useAuthActions = () => {
  const { push } = useIonRouter();
  const { setAuthStatus, clearSession } = useAuthContext();
  const { setSecureValue, getSecureValue } = useStorage();
  const { getStoredToken, clearToken } = usePushNotifications();

  const persistSession = async (data: { token: string; refreshToken: string }) => {
    const { token, refreshToken } = data;

    await Promise.all([
      setSecureValue(STORAGE_KEYS.token, token),
      setSecureValue(STORAGE_KEYS.refreshToken, refreshToken),
    ]);
  };

  const login = async (body: LoginReq): Promise<ApiRes> => {
    try {
      const { data, success, error } = await loginReq(body);
      if (!success) return { success: false, error: getLoginErrorMessage(error) };

      // Limpiar caché del usuario anterior antes de iniciar nueva sesión
      clearAllSWRCache();

      await persistSession(data);
      activatePendingDiagnosticsAccessForLogin(body.email);
      schedulePostLoginNotificationPermissionPrompt();
      setAuthStatus(true);
      return { success: true };
    } catch (error) {
      console.error('Login failed', error);
      return { success: false, error: getLoginErrorMessage(error) };
    }
  };

  type LogoutOptions = {
    skipRemoteLogout?: boolean;
    skipTrackingFlush?: boolean;
  };

  const logout = async (options: LogoutOptions = {}): Promise<boolean> => {
    try {
      // Obtener tokens antes de limpiar para enviarlos al backend
      const refreshToken = (await getSecureValue(STORAGE_KEYS.refreshToken)) ?? '';
      const firebase_token = await getStoredToken();

      if (!options.skipTrackingFlush) {
        await flushTrackingBeforeLogout();
      }

      // Limpiar sesión local primero para UX inmediata
      clearActiveDiagnosticsAccess();
      await Promise.all([clearToken(), clearSession()]);
      clearAllSWRCache();
      setAuthStatus(false);
      push('/auth');

      if (!options.skipRemoteLogout) {
        // Notificar al backend en background (best-effort)
        const body: LogoutReq = { refreshToken };
        if (firebase_token) {
          body.firebase_token = firebase_token;
        }

        logoutReq(body).catch((error) => {
          console.warn('[Logout] API fallido (sesión local ya cerrada):', error);
        });
      }

      return true;
    } catch (error) {
      console.error('Logout error:', error);
      // Forzar cierre local aunque falle algo inesperado
      clearAllSWRCache();
      setAuthStatus(false);
      push('/auth');
      return false;
    }
  };

  const register = (body: RegisterReq): Promise<ApiRes> =>
    handleAuthRequest(() => registerReq(body), 'register');

  const password = (body: PasswordReq): Promise<ApiRes> =>
    handleAuthRequest(() => passwordReq(body), 'password');

  return { login, logout, register, password };
};
